import { useCallback, useEffect, useRef, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';
import { toggleMultiSelectOption } from '@backrs_ui/grow-ui/src/utils/multiSelectOptions';

import { Box, SxProps, Theme } from '@mui/material';

import { PromptButtons, PromptOption } from './PromptButtons';
import { SkipChatButton } from './SkipChatButton';

type MultiSelectChatQuestionProps = Readonly<{
  idPrefix?: string;
  options: readonly string[];
  exclusiveOptions?: readonly string[];
  required: boolean;
  isTyping: boolean;
  onSubmit: (selected: string[]) => void;
  onSkip: () => void;
}>;

export const MultiSelectChatQuestion = ({
  idPrefix = 'multi-select-chat-question',
  options,
  exclusiveOptions,
  required,
  isTyping,
  onSubmit,
  onSkip,
}: MultiSelectChatQuestionProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleOption = (title: string) => {
    setSelected((prev) => toggleMultiSelectOption(prev, title, exclusiveOptions));
  };

  const promptOptions: PromptOption[] = options.map((option, i) => ({
    id: String(i),
    title: option,
  }));

  const selectedIds = selected
    .map((title) => String(options.indexOf(title)))
    .filter((id) => id !== '-1');

  // Submit should always require at least one selection — an empty submit conveys no
  // answer. If the question is optional, the Skip button is the way to move past it.
  const isDisabled = isTyping || selected.length === 0;

  // Mirror `selected` into a ref so handlers (button click, keydown listener) always
  // read the latest committed value, not a snapshot from a prior render's closure.
  const selectedRef = useRef(selected);

  selectedRef.current = selected;

  const handleSubmit = useCallback(() => onSubmit(selectedRef.current), [onSubmit]);

  // Listen for Enter at the container level in capture phase, so we run before the
  // focused option chip's own keydown — otherwise MUI's "Enter activates focused
  // button" behavior would re-fire the chip's onClick and deselect the option.
  useEffect(() => {
    if (isDisabled) return;

    const container = containerRef.current;

    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    };

    container.addEventListener('keydown', handleKeyDown, true);

    return () => container.removeEventListener('keydown', handleKeyDown, true);
  }, [isDisabled, handleSubmit]);

  return (
    <Box ref={containerRef} sx={containerSx}>
      <PromptButtons
        onClick={(option) => toggleOption(option.title)}
        isTyping={isTyping}
        promptOptions={promptOptions}
        selectedOptionIds={selectedIds}
        variant="SINGLE_SELECT"
      />
      <Box sx={actionsRowSx}>
        <EnterButton
          id={`${idPrefix}-submit`}
          onClick={handleSubmit}
          label={appStrings.modules.solutionSessions.questions.submit}
          showEnterHint
          disabled={isDisabled}
        />
        {required ? (
          <Box />
        ) : (
          <SkipChatButton id={`${idPrefix}-skip`} disabled={isTyping} onSkip={onSkip} />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const actionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};
